import { useEffect, useRef, type ReactNode } from 'react';
import { keyBelongsToField } from '../../ui/keys';
import { IconButton } from '../../ui/controls';
import { ASSETS_WIDTH, LIBRARY_RAIL_WIDTH } from '../content/Library';

/** What the leading panel shows: Layers, Assets, or a file's version history (W5-AR1). */
export type NavigationView = 'layers' | 'assets' | 'versions';
/** History retains its own compact view; Layers and Assets both use Studio rail. */
export const NAVIGATION_WIDTH = typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches ? 260 : 240;

/** The Layers content closes independently of the persistent Studio rail. */
export function NavigationPanel({ onClose, children }: {
  onClose: () => void;
  children: ReactNode;
}) {
  const panel = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  // The panel takes the keyboard as it opens; focus already inside it (a field, a row) is
  // left where it is.
  useEffect(() => {
    const element = panel.current;
    if (element && !element.contains(document.activeElement)) element.focus({ preventScroll: true });
  }, []);
  /**
   * Escape closes it wherever focus is — the panel, the canvas, a button just pressed —
   * unless something else took that Escape first: an open menu or popover, a pen path given
   * up, a pick dropped (each prevents the key's default). Read once the key has been
   * everywhere, since which listener runs first is not ours to count on. A name being
   * typed keeps its Escape. It used to close only through the page's own shortcut handler,
   * and stayed open (QA 2026-10-05, ART-LAY-01; review E5).
   */
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || keyBelongsToField(event.target, event.key)) return;
      window.setTimeout(() => {
        if (!event.defaultPrevented) closeRef.current();
      });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  return (
    <div ref={panel} tabIndex={-1} className="workspace-navigation workspace-navigation-content workspace-library glass ui-pop pointer-events-auto absolute z-20 flex overflow-hidden outline-none" style={{ width: ASSETS_WIDTH - LIBRARY_RAIL_WIDTH }}>
      <aside aria-label="Layers" className="library-browser flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        <header className="library-panel-header flex shrink-0 items-center gap-[8px]">
          <h2 className="min-w-0 flex-1 truncate font-semibold text-label">Layers</h2>
          <IconButton icon="close" label="Close Layers" onClick={onClose} />
        </header>
        {children}
      </aside>
    </div>
  );
}
